import { useAuth } from '@on3/ui-lib/contexts/AuthProvider';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { getReferrer, getUserAgent } from '@on3/ui-lib/utils/tracking';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './ExcoPlayer.module.scss';

declare global {
  interface Window {
    ExCoPlayer?: {
      connect: (playerId?: string) => ExCoPlayerApi;
    };
    getSubId?: () => string;
  }
}

type ExCoPlayerApi = {
  init: (options: { sticky: object; variation: string }) => Promise<void>;
  destroy: () => void;
  setStickyState: (state: boolean) => void;
};

const SCRIPT_ID = 'exco-player-script';

const loadExcoScript = (playerId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      return resolve();
    }

    const script = document.createElement('script');

    script.src = `//player.ex.co/player/${playerId}`;
    script.async = true;
    script.id = SCRIPT_ID;
    script.className = 'exco-player';
    script.setAttribute('programmatic', 'true');

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load ExcoPlayer script'));

    document.body.appendChild(script);
  });
};

export const ExcoPlayer = ({
  playerId = '34c93dc6-f9c2-4ad1-85ce-def4a929874b', // default player ID
  className,
}: {
  playerId?: string;
  className?: string;
}) => {
  const { user } = useAuth();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const playerApi = useRef<ExCoPlayerApi | null>(null);
  const { currentSite } = useSite();

  const isPremiumUser = user?.st?.includes('premium') || false;

  const exsConfig = useMemo(
    () =>
      JSON.stringify({
        customParams: {
          siteKey: currentSite.key,
        },
      }),
    [currentSite.key],
  );

  useEffect(() => {
    window.getSubId = () => currentSite?.url || 'unknown';
  }, [currentSite?.url]);

  useEffect(() => {
    return () => {
      playerApi.current?.destroy();
    };
  }, [playerApi]);

  // Load the script only once
  useEffect(() => {
    if (isPremiumUser) {
      return;
    }

    loadExcoScript(playerId)
      .then(() => {
        setIsScriptLoaded(true);
      })
      .catch((error) => {
        console.error('ExcoPlayer script failed to load:', error);
      });
  }, [isPremiumUser, playerId]);

  // Re-init the player on route change
  useEffect(() => {
    if (!isScriptLoaded || isPremiumUser) return;

    const isTwitterReferral =
      getUserAgent()?.includes('twitter') || getReferrer() === 'twitter';
    const floatPlayer = user?.has || isTwitterReferral;

    const initPlayer = async () => {
      if (!window.ExCoPlayer) {
        console.warn('ExCoPlayer is not available yet');

        return;
      }

      // Remove existing script to ensure fresh load
      const existingScript = document.getElementById(SCRIPT_ID);

      if (existingScript) {
        existingScript.remove();

        await loadExcoScript(playerId);
      }

      playerApi.current = window.ExCoPlayer.connect(playerId);

      if (!playerApi.current) return;

      playerApi.current
        .init({
          sticky: {
            mode: floatPlayer ? 'custom' : 'non-persistent-in-view',
          },
          variation: user?.has ? 'subscriber' : 'guest',
        })
        .then(() => {
          if (user?.has) {
            playerApi.current?.setStickyState(false);
          }
        })
        .catch((err) => {
          console.error('Failed to init player:', err);
        });
    };

    initPlayer();
  }, [isScriptLoaded, user?.has, isPremiumUser, playerId]);

  if (isPremiumUser) return null;

  return (
    <div className={clsx(styles.container, className)}>
      <div
        data-exs-config={exsConfig}
        id={playerId}
        subid-function="getSubId"
      />
    </div>
  );
};
